import { yupResolver } from "@hookform/resolvers/yup";
import type { FC } from "react";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { registrationSchema } from "~/features/registration-form/registration.schema";
import { Eye } from "~/icons";
import { Button } from "~/shared/ui-kit";
import { Card } from "~/shared/ui-kit/card/Card";
import { Checkbox } from "~/shared/ui-kit/checkbox/Checkbox";
import { Markdown } from "~/shared/ui-kit/markdown/Markdown";
import { TextField } from "~/shared/ui-kit/text-field/TextField";
import { Title } from "~/shared/ui-kit/title/Title";
import { Typography } from "~/shared/ui-kit/typography/Typography";

import styles from "./RegistrationForm.module.css";

export type RegistrationData = {
  form_title: string;
  form_description_text: string;
  form_email_input_label: string;
  form_nickname_input_label: string;
  form_password_input_label: string;
  form_promo_code_button_text: string;
  form_promo_code_input_label: string;
  form_remember_checkbox_text?: string;
  form_submit_button_text: string;
  validation_invalid_email: string;
  validation_nickname_max_length: string;
  validation_nickname_min_length: string;
  validation_password_max_width: string;
  validation_password_min_width: string;
  validation_required: string;
  description_link_text: string;
  description_text: string;
};

export type RegistrationFormValues = {
  nick: string;
  email: string;
  password: string;
  bonusCode?: string;
  isRememberPassword?: boolean;
};

export type RegistrationServerErrors = Partial<
  Record<keyof RegistrationFormValues, string>
>;

export type RegistrationFormProps = {
  data: RegistrationData;
  onSubmit: (data: RegistrationFormValues) => void;
  serverErrors?: RegistrationServerErrors;
};

export const RegistrationForm: FC<RegistrationFormProps> = ({
  data,
  onSubmit,
  serverErrors,
}) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<RegistrationFormValues>({
    mode: "onChange",
    defaultValues: { isRememberPassword: false },
    resolver: yupResolver(registrationSchema(data)),
  });

  const [passwordIsVisible, setPasswordIsVisible] = useState(false);
  const [isBonusCodeOpen, setIsBonusCodeOpen] = useState(false);

  const [serverErrorsState, setServerErrorsState] = useState<
    RegistrationServerErrors | undefined
  >(serverErrors);

  useEffect(() => {
    setServerErrorsState(serverErrors);
  }, [serverErrors]);

  return (
    <Card
      className="rounded-2xl md:pb-8 md:rounded-lg max-w-[440px] md:max-w-[525px]"
      variant="secondary"
    >
      <form
        className="px-4 py-6 md:py-8 md:px-10"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Title
          as="h2"
          size="small"
          weight="semibold"
          className="text-center mb-4 md:mb-6"
        >
          {data.form_title}
        </Title>

        <Controller
          control={control}
          name="nick"
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(e) => {
                setServerErrorsState({ ...serverErrorsState, nick: undefined });
                field.onChange(e);
              }}
              label={data.form_nickname_input_label}
              errorMessage={errors.nick?.message ?? serverErrorsState?.nick}
            />
          )}
        />

        <Controller
          control={control}
          name="email"
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(e) => {
                setServerErrorsState({
                  ...serverErrorsState,
                  email: undefined,
                });
                field.onChange(e);
              }}
              label={data.form_email_input_label}
              errorMessage={errors.email?.message ?? serverErrorsState?.email}
            />
          )}
        />

        <Controller
          control={control}
          name="password"
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(e) => {
                setServerErrorsState({
                  ...serverErrorsState,
                  password: undefined,
                });
                field.onChange(e);
              }}
              label={data.form_password_input_label}
              type={passwordIsVisible ? "text" : "password"}
              errorMessage={
                errors.password?.message ?? serverErrorsState?.password
              }
              icon={
                <Eye
                  onClick={() => setPasswordIsVisible(!passwordIsVisible)}
                  className="cursor-pointer"
                  viewBox="0 0 24 24"
                />
              }
            />
          )}
        />

        {!isBonusCodeOpen ? (
          <Button
            variant="transparent"
            type="button"
            onPress={() => setIsBonusCodeOpen(true)}
            size="medium"
            className="w-full text-center underline border-none h-[60px] mb-4 md:mb-6 hover:bg-transparent hover:text-white"
          >
            {data.form_promo_code_button_text}
          </Button>
        ) : (
          <Controller
            control={control}
            name="bonusCode"
            render={({ field }) => (
              <TextField
                {...field}
                onChange={(e) => {
                  setServerErrorsState({
                    ...serverErrorsState,
                    bonusCode: undefined,
                  });
                  field.onChange(e);
                }}
                className="!mb-4 md:mb-6"
                label={data.form_promo_code_input_label}
                errorMessage={
                  errors.bonusCode?.message ?? serverErrorsState?.bonusCode
                }
              />
            )}
          />
        )}

        <Controller
          control={control}
          name="isRememberPassword"
          rules={{ required: false }}
          render={({ field, fieldState: { error } }) => (
            <Checkbox
              aria-label="label"
              className="mb-4 md:mb-6"
              errorMessage={error?.message}
              {...field}
              onChange={(e) => {
                setServerErrorsState({
                  ...serverErrorsState,
                  isRememberPassword: undefined,
                });
                field.onChange(e);
              }}
            >
              <Typography
                weight="normal"
                size="small"
                md={{ size: "medium" }}
                as="span"
              >
                {data?.form_remember_checkbox_text ?? "Remember password"}
              </Typography>
            </Checkbox>
          )}
        />

        <Markdown className={styles.formDescriptionMarkdown}>
          {data.form_description_text}
        </Markdown>

        <Button
          type="submit"
          size="small"
          weight="bold"
          md={{ size: "medium" }}
          className="w-full py-3 md:py-[17px]"
          isDisabled={Object.values(serverErrorsState ?? {}).some(
            (item) => !!item,
          )}
        >
          {data.form_submit_button_text}
        </Button>
      </form>
    </Card>
  );
};
